import React, { useEffect, useMemo, useRef, useState } from 'react';
import useShippingSettings from './useShippingSettings';
import Alert from '../../Alert';
import ModeStep from './steps/ModeStep';
import PackageStep from './steps/PackageStep';
import MethodsStep from './steps/MethodsStep';
import RatesStep from './steps/RatesStep';
import PickupStep from './steps/PickupStep';
import CombinedStep from './steps/CombinedStep';
import SummaryStep, { getShippingIssues } from './steps/SummaryStep';
import { validateCanadianPostalCode } from '../../../helpers/canadianLocation';
import { getAllowedShippingMethods } from './shippingMethodOptions';
import useDebouncedAutosave from '../Product/useDebouncedAutosave';
import './ShippingWizard.css';
import '../WizardProgress.css';

const STEP_LABELS = {
    mode: 'Mode',
    package: 'Package',
    methods: 'Methods',
    rates: 'Rates',
    pickup: 'Pickup',
    combined: 'Combined',
    summary: 'Summary',
};

const positive = (value) => parseFloat(value) > 0;

const getPrefillDismissKey = (productUuid) => `shipping-defaults-prefill-dismissed:${productUuid}`;

const readPrefillDismissed = (productUuid) => {
    if (!productUuid || typeof window === 'undefined' || !window.localStorage) return false;
    try {
        return window.localStorage.getItem(getPrefillDismissKey(productUuid)) === 'true';
    } catch (e) {
        return false;
    }
};

const writePrefillDismissed = (productUuid) => {
    if (!productUuid || typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(getPrefillDismissKey(productUuid), 'true');
    } catch (e) {
        // localStorage can be unavailable in private browsing or tests.
    }
};

const stepError = (stepKey, state, shipToCountries) => {
    switch (stepKey) {
        case 'mode':
            return state.mode ? '' : 'Choose a shipping pricing option.';
        case 'package':
            if (!state.originZIPCode || String(state.originZIPCode).trim() === '') {
                return state.originCountry === 'CA'
                    ? 'Enter the postal code you ship from.'
                    : 'Enter the ZIP code you ship from.';
            }
            if (
                state.originCountry === 'CA'
                && !validateCanadianPostalCode(state.originZIPCode)
            ) {
                return 'Enter a valid Canadian postal code in A1A 1A1 format.';
            }
            if (state.mode === 'pickup') return '';
            if (!positive(state.weight)) return 'Enter a package weight greater than zero.';
            if (!positive(state.length) || !positive(state.width) || !positive(state.height)) {
                return 'Enter package length, width, and height greater than zero.';
            }
            return '';
        case 'methods':
            if ((state.methods || []).length === 0) return 'Select at least one shipping method.';
            if ((state.methods || []).includes('LOCAL_PICKUP') && !state.pickupAddress?.trim()) {
                return 'Enter the pickup address for local pickup.';
            }
            return '';
        case 'rates': {
            const missingRateMethod = (state.methods || [])
                .filter((method) => method !== 'LOCAL_PICKUP')
                .find((method) => !positive(state.rates?.[method]));
            return missingRateMethod ? 'Enter a flat rate for every selected shipping method.' : '';
        }
        case 'pickup':
            return state.pickupAddress?.trim() ? '' : 'Enter the pickup address.';
        case 'combined':
            if (!state.allowCombinedShipping) return '';
            return parseFloat(state.secondaryRate) >= 0 ? '' : 'Enter a secondary item rate of zero or more.';
        case 'summary':
            return getShippingIssues(state, { shipToCountries }).length === 0
                ? ''
                : 'Resolve the issues above before continuing.';
        default:
            return '';
    }
};

function stepsForMode(mode) {
    switch (mode) {
        case 'none':
            return ['mode', 'summary'];
        case 'pickup':
            return ['mode', 'package', 'pickup', 'summary'];
        case 'flat':
            return ['mode', 'package', 'methods', 'rates', 'combined', 'summary'];
        case 'calculated':
            return ['mode', 'package', 'methods', 'combined', 'summary'];
        default:
            return ['mode'];
    }
}

function ShippingProgressBar({ steps, currentStepIndex, completedSteps, canJumpFreely, onStepClick }) {
    const currentStepRef = useRef(null);

    useEffect(() => {
        currentStepRef.current?.scrollIntoView?.({
            block: 'nearest',
            inline: 'center',
        });
    }, [currentStepIndex, steps.length]);

    return (
        <nav
            className="wizard-progress shipping-progress"
            aria-label="Shipping setup progress"
        >
            {steps.map((step, index) => {
                const isActive = index === currentStepIndex;
                const isDone = canJumpFreely || completedSteps.has(index);
                const isClickable = (canJumpFreely || isDone) && !isActive;
                const className = [
                    'wizard-progress-step',
                    'shipping-progress-step',
                    isActive ? 'current' : '',
                    isDone ? 'done' : '',
                    isClickable ? 'clickable' : '',
                ].filter(Boolean).join(' ');
                return (
                    <button
                        key={`${step}-${index}`}
                        ref={isActive ? currentStepRef : undefined}
                        type="button"
                        className={className}
                        onClick={isClickable ? () => onStepClick(index) : undefined}
                        disabled={!isClickable}
                        aria-label={`${index + 1}. ${STEP_LABELS[step]}`}
                        aria-current={isActive ? 'step' : undefined}
                        title={isClickable ? `Jump to ${STEP_LABELS[step]}` : undefined}
                    >
                        <span
                            className="wizard-progress-dot shipping-progress-dot"
                            aria-hidden="true"
                        >
                            {index + 1}
                        </span>
                        <span>{STEP_LABELS[step]}</span>
                    </button>
                );
            })}
        </nav>
    );
}

export default function ShippingWizard({
    productUuid = 'demo',
    onComplete,
    onBack,
    onSaveAndExit,
    embedded = false,
    summaryTools = null,
    originCountry = null,
    shipToCountries = ['US'],
}) {
    const settings = useShippingSettings(productUuid, originCountry);
    const {
        state,
        setField,
        setFields,
        save,
        saving,
        error,
        loading,
        examples,
        examplesLoading,
        examplesError,
        fetchExamples,
        shippingDefaults,
        shippingDefaultsLoading,
        shippingDefaultsError,
        fetchShippingDefaults,
        isFirstTime,
    } = settings;
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [prefillDismissed, setPrefillDismissed] = useState(() => readPrefillDismissed(productUuid));
    const [prefillRequested, setPrefillRequested] = useState(false);
    const steps = useMemo(() => stepsForMode(state.mode), [state.mode]);
    const currentStep = steps[currentStepIndex] || steps[0];
    const currentError = stepError(currentStep, state, shipToCountries);
    const isFinalStep = currentStepIndex === steps.length - 1;
    const canJumpFreely = !isFirstTime;
    const initialJumpDone = useRef(false);
    const {cancelAutosave} = useDebouncedAutosave(
        state,
        async () => {
            const saved = await save();
            if (!saved) throw new Error('Failed to autosave shipping settings');
        },
        {enabled: !loading && Boolean(productUuid)},
    );

    useEffect(() => {
        if (!initialJumpDone.current) return;
        setCurrentStepIndex(0);
        setCompletedSteps(new Set());
    }, [steps]);

    useEffect(() => {
        if (loading || initialJumpDone.current) return;
        initialJumpDone.current = true;
        if (!isFirstTime) {
            setCurrentStepIndex(steps.length - 1);
        }
    }, [loading, isFirstTime, steps.length]);

    useEffect(() => {
        setPrefillDismissed(readPrefillDismissed(productUuid));
        setPrefillRequested(false);
    }, [productUuid]);

    useEffect(() => {
        if (!isFirstTime || currentStep !== 'mode' || prefillDismissed || prefillRequested) return;
        setPrefillRequested(true);
        fetchShippingDefaults();
    }, [
        currentStep,
        fetchShippingDefaults,
        isFirstTime,
        prefillDismissed,
        prefillRequested,
    ]);

    useEffect(() => {
        if (currentStepIndex > steps.length - 1) {
            setCurrentStepIndex(Math.max(0, steps.length - 1));
        }
        setCompletedSteps((previous) => new Set([...previous].filter((index) => index < steps.length)));
    }, [currentStepIndex, steps.length]);

    useEffect(() => {
        if (state.originCountry !== 'CA') return;

        const allowedMethods = new Set(getAllowedShippingMethods({
            originCountry: state.originCountry,
            shipToCountries,
            mode: state.mode,
        }));
        const compatibleMethods = (state.methods || [])
            .filter(method => allowedMethods.has(method));

        if (compatibleMethods.length !== (state.methods || []).length) {
            setField('methods', compatibleMethods);
        }
    }, [setField, shipToCountries, state.methods, state.mode, state.originCountry]);

    const handleBack = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex((index) => index - 1);
            return;
        }
        onBack?.();
    };

    const handleNext = async () => {
        if (currentError) return;

        cancelAutosave();
        const saved = await save();
        if (!saved) return;

        setCompletedSteps((previous) => new Set([...previous, currentStepIndex]));

        if (isFinalStep) {
            if (onComplete) {
                onComplete(null);
                return;
            }
            window.location.href = '/account/products';
            return;
        }

        setCurrentStepIndex((index) => Math.min(index + 1, steps.length - 1));
    };

    const handleSaveAndExit = async () => {
        cancelAutosave();
        const saved = await save();
        if (!saved) return;

        if (onSaveAndExit) {
            onSaveAndExit(null);
            return;
        }

        window.location.href = '/account/products';
    };

    const handleApplyDefaults = () => {
        if (!shippingDefaults?.defaults) return;
        setFields(shippingDefaults.defaults);
        setPrefillDismissed(true);
        writePrefillDismissed(productUuid);
    };

    const handleDismissPrefill = () => {
        setPrefillDismissed(true);
        writePrefillDismissed(productUuid);
    };

    const sharedStepProps = {
        state,
        setField,
        setFields,
        shipToCountries,
    };

    const renderStep = () => {
        switch (currentStep) {
            case 'mode':
                return <ModeStep {...sharedStepProps} />;
            case 'package':
                return <PackageStep {...sharedStepProps} />;
            case 'methods':
                return <MethodsStep {...sharedStepProps} />;
            case 'rates':
                return (
                    <RatesStep
                        {...sharedStepProps}
                        examples={examples}
                        examplesLoading={examplesLoading}
                        fetchExamples={fetchExamples}
                    />
                );
            case 'pickup':
                return <PickupStep {...sharedStepProps} />;
            case 'combined':
                return <CombinedStep {...sharedStepProps} />;
            case 'summary':
                return (
                    <SummaryStep
                        {...sharedStepProps}
                        examples={examples}
                        examplesLoading={examplesLoading}
                        examplesError={examplesError}
                        fetchExamples={fetchExamples}
                        shipToCountries={shipToCountries}
                    />
                );
            default:
                return null;
        }
    };

    if (loading) {
        return <p>Loading...</p>;
    }

    return (
        <div className={`shipping-wizard${embedded ? ' embedded' : ' full-page'}`}>
            {!embedded && <h2>Shipping Settings</h2>}

            <ShippingProgressBar
                steps={steps}
                currentStepIndex={currentStepIndex}
                completedSteps={completedSteps}
                canJumpFreely={canJumpFreely}
                onStepClick={setCurrentStepIndex}
            />

            {error && (
                <div className="flash error">
                    <p>{error}</p>
                </div>
            )}

            {isFirstTime && currentStep === 'mode' && !prefillDismissed && shippingDefaults?.defaults && (
                <div className="shipping-prefill-banner">
                    <p>
                        Want a head start? We can copy your shipping settings from{' '}
                        <strong>{shippingDefaults.sourceProduct?.name || 'a previous product'}</strong>.
                    </p>
                    <div className="shipping-prefill-actions">
                        <button
                            type="button"
                            className="button primary"
                            onClick={handleApplyDefaults}
                            disabled={shippingDefaultsLoading}
                        >
                            Apply
                        </button>
                        <button
                            type="button"
                            className="button"
                            onClick={handleDismissPrefill}
                        >
                            No thanks
                        </button>
                    </div>
                </div>
            )}

            {isFirstTime && currentStep === 'mode' && !prefillDismissed && shippingDefaultsError && (
                <Alert type="error" className="shipping-step-error">
                    <p>{shippingDefaultsError}</p>
                </Alert>
            )}

            {renderStep()}

            {currentError && (
                <Alert type="error" className="shipping-step-error">
                    <p>{currentError}</p>
                </Alert>
            )}

            {currentStep === 'summary' && summaryTools && (
                <div className="shipping-summary-tools">
                    {summaryTools}
                </div>
            )}

            <div className="wizard-nav">
                <button
                    type="button"
                    className="button"
                    onClick={handleBack}
                    disabled={saving || (!onBack && currentStepIndex === 0)}
                >
                    Back
                </button>
                <button
                    type="button"
                    className="button secondary"
                    onClick={handleSaveAndExit}
                    disabled={saving}
                >
                    Save and Edit Later
                </button>
                <button
                    type="button"
                    className="button primary"
                    onClick={handleNext}
                    disabled={saving || !!currentError}
                >
                    {saving ? 'Saving...' : isFinalStep ? (embedded && onComplete ? 'Next' : 'Done') : 'Next'}
                </button>
            </div>
        </div>
    );
}
